// Referral Report — guests bucketed by acquisition channel for the
// selected Bali date range. Matches the look + chrome of every other
// report under the Customers tab (ReportShell + a Date-range card).
// A local preset list is used because the user-facing calendar labels
// requested here (This Week / Last Week / This Month / Last Month /
// This Year / Last Year) differ from the shared DateRangeFilter's
// day-counted presets ("Last 7 days", "Last 30 days", "Last 365 days").
// Shared `DateRangeFilter.jsx` is NOT modified — every other report
// keeps its existing presets byte-for-byte.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, ChevronDown, ChevronRight, Users } from "lucide-react";
import { toast } from "sonner";
import { staffApi, formatApiError } from "@/lib/api";
import ReportShell from "./ReportShell";

const PRESETS = [
  { key: "today",      label: "Today" },
  { key: "yesterday",  label: "Yesterday" },
  { key: "this_week",  label: "This Week" },
  { key: "last_week",  label: "Last Week" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "this_year",  label: "This Year" },
  { key: "last_year",  label: "Last Year" },
  { key: "custom",     label: "Custom Date" },
];

const BALI_TZ = "Asia/Makassar";

function todayBali() {
  return new Date().toLocaleDateString("en-CA", { timeZone: BALI_TZ });
}
function parseBali(dateStr) {
  // dateStr "YYYY-MM-DD" anchored at Bali NOON (04:00 UTC). A midnight
  // anchor sits at 16:00 UTC on the PREVIOUS day, so every `getUTC*` /
  // `toISOString()` read came back one day early — that was the
  // "Yesterday shows two days ago" bug, and it skewed the week / month /
  // year boundaries the same way.
  return new Date(`${dateStr}T12:00:00+08:00`);
}
function fmt(d) {
  // Date → "YYYY-MM-DD" read in BALI time, never the UTC calendar date.
  return d.toLocaleDateString("en-CA", { timeZone: BALI_TZ });
}
function addDaysBali(dateStr, days) {
  const d = parseBali(dateStr);
  d.setUTCDate(d.getUTCDate() + days);
  return fmt(d);
}
function startOfWeekMonday(dateStr) {
  // Monday as week start (locale-neutral, operator mental model).
  const d = parseBali(dateStr);
  const dow = d.getUTCDay(); // 0 = Sun .. 6 = Sat
  const back = dow === 0 ? 6 : dow - 1;
  d.setUTCDate(d.getUTCDate() - back);
  return fmt(d);
}
function rangeForPreset(preset, customFrom, customTo) {
  const today = todayBali();
  switch (preset) {
    case "today":
      return { date_from: today, date_to: today };
    case "yesterday": {
      const y = addDaysBali(today, -1);
      return { date_from: y, date_to: y };
    }
    case "this_week": {
      const start = startOfWeekMonday(today);
      return { date_from: start, date_to: today };
    }
    case "last_week": {
      const thisStart = startOfWeekMonday(today);
      const lastStart = addDaysBali(thisStart, -7);
      const lastEnd = addDaysBali(thisStart, -1);
      return { date_from: lastStart, date_to: lastEnd };
    }
    case "this_month": {
      const d = parseBali(today);
      const start = fmt(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)));
      return { date_from: start, date_to: today };
    }
    case "last_month": {
      const d = parseBali(today);
      const lastStart = fmt(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1)));
      const lastEnd = fmt(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 0)));
      return { date_from: lastStart, date_to: lastEnd };
    }
    case "this_year": {
      const d = parseBali(today);
      const start = fmt(new Date(Date.UTC(d.getUTCFullYear(), 0, 1)));
      return { date_from: start, date_to: today };
    }
    case "last_year": {
      const d = parseBali(today);
      const start = fmt(new Date(Date.UTC(d.getUTCFullYear() - 1, 0, 1)));
      const end = fmt(new Date(Date.UTC(d.getUTCFullYear() - 1, 11, 31)));
      return { date_from: start, date_to: end };
    }
    case "custom":
      return { date_from: customFrom, date_to: customTo };
    default:
      return { date_from: today, date_to: today };
  }
}

function prettyDate(d) {
  if (!d) return "—";
  try {
    return new Date(`${d}T00:00:00+08:00`).toLocaleDateString("en-GB", {
      timeZone: BALI_TZ, day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return d; }
}

export default function ReferralReport() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [preset, setPreset] = useState("this_month"); // user spec: default This Month
  const [customFrom, setCustomFrom] = useState(todayBali());
  const [customTo, setCustomTo] = useState(todayBali());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const range = useMemo(
    () => rangeForPreset(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranch(r.data.find((b) => b.id === bid) || null));
  }, [bid]);

  const load = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const r = await staffApi.get(`/reports/branch/${bid}/referrals`, { params: range });
      setData(r.data);
      // Auto-expand the busiest channel so the operator sees guest
      // names without a second tap.
      const busiest = (r.data?.channels || []).reduce((best, c) => (
        (!best || c.count > best.count) ? c : best
      ), null);
      if (busiest && busiest.count > 0) {
        setExpanded((e) => ({ ...e, [busiest.key]: true }));
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [bid, range]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  const exportSpec = useMemo(() => {
    if (!data) return null;
    const rows = [];
    for (const ch of data.channels || []) {
      for (const g of ch.guests) {
        rows.push({
          channel: ch.label,
          name: g.customer_name,
          registered: prettyDate(g.registered_date),
          visits: g.total_visits,
          referrer: g.referrer_name || "",
        });
      }
    }
    return {
      filename: "referral-report",
      range: { date_from: data.date_from, date_to: data.date_to },
      subtitle: `${data.total} guest${data.total === 1 ? "" : "s"} across ${(data.channels || []).filter((c) => c.count > 0).length} channel(s)`,
      columns: [
        { header: "Channel", key: "channel" },
        { header: "Guest", key: "name" },
        { header: "Registered", key: "registered" },
        { header: "Total Visits", key: "visits" },
        { header: "Referrer", key: "referrer" },
      ],
      rows,
    };
  }, [data]);

  return (
    <ReportShell
      title="Referral Report"
      subtitle="New guests grouped by how they heard about Cocoon, for the selected Bali date range. Click a channel to see every guest acquired through it."
      branchName={branch?.name}
      tab="customers"
      exportSpec={exportSpec}
    >
      <div className="space-y-4">
        {/* Date-range filter — matches the look of the shared card,
            but exposes the user-specified preset labels. */}
        <Card className="rounded-lg">
          <CardContent className="p-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">Date range</span>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger className="w-48" data-testid="referral-date-preset"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRESETS.map((p) => (
                  <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {preset === "custom" && (
              <>
                <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="w-40" data-testid="referral-custom-from" />
                <span className="text-xs text-muted-foreground">to</span>
                <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="w-40" data-testid="referral-custom-to" />
              </>
            )}
            <Button size="sm" variant="outline" onClick={load} disabled={loading} className="gap-1 ml-auto" data-testid="referral-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </CardContent>
        </Card>

        {/* Summary strip — channel counts at a glance. */}
        <Card className="rounded-lg overflow-hidden" data-testid="referral-summary-card">
          <CardContent className="p-0">
            <div className="p-4 border-b border-border flex items-baseline justify-between">
              <h2 className="text-sm font-semibold">Guest Acquisition</h2>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">total</div>
                <div className="text-lg font-semibold" data-testid="referral-total">{data?.total ?? 0}</div>
              </div>
            </div>
            <div className="p-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
              {(data?.channels || []).map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className={`text-left rounded-md border px-3 py-2 transition-colors ${expanded[c.key] ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                  onClick={() => toggle(c.key)}
                  data-testid={`referral-channel-chip-${c.key}`}
                >
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</div>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-lg font-semibold" data-testid={`referral-channel-count-${c.key}`}>{c.count}</span>
                    {expanded[c.key] ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Per-channel expandable guest tables. */}
        {(data?.channels || []).map((c) => (
          <Card
            key={c.key}
            className={`rounded-lg overflow-hidden ${expanded[c.key] ? "" : "hidden"}`}
            data-testid={`referral-channel-card-${c.key}`}
          >
            <CardContent className="p-0">
              <div className="p-4 border-b border-border flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold">{c.label}</h3>
                <Badge variant="outline" className="text-[10px]">{c.count} guest{c.count === 1 ? "" : "s"}</Badge>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="uppercase text-[10px] tracking-wider">Guest</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider">Registered</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider text-right">Total Visits</TableHead>
                    {c.key === "referral" && (
                      <TableHead className="uppercase text-[10px] tracking-wider">Referred By</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {c.count === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={c.key === "referral" ? 4 : 3}
                        className="text-center text-sm text-muted-foreground py-6"
                      >
                        {loading ? "Loading…" : "No guests in this range."}
                      </TableCell>
                    </TableRow>
                  ) : c.guests.map((g) => (
                    <TableRow key={g.customer_id} data-testid={`referral-guest-row-${g.customer_id}`}>
                      <TableCell className="text-sm">
                        <Link to={`/staff/brand/customers/${g.customer_id}`} className="text-primary hover:underline">
                          {g.customer_name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{prettyDate(g.registered_date)}</TableCell>
                      <TableCell className="text-xs text-right">{g.total_visits}</TableCell>
                      {c.key === "referral" && (
                        <TableCell className="text-sm">
                          {g.referrer_id ? (
                            <Link
                              to={`/staff/brand/customers/${g.referrer_id}`}
                              className="text-primary hover:underline"
                              data-testid={`referral-referrer-link-${g.customer_id}`}
                            >
                              {g.referrer_name || "—"}
                            </Link>
                          ) : (
                            <span className="text-muted-foreground italic">unattributed</span>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))}
      </div>
    </ReportShell>
  );
}
